import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { HealthieFormModule } from "@/lib/api/hooks";
import { cn } from "@/lib/cn";

export interface IntakeFormFieldProps {
  module: HealthieFormModule;
  value: string;
  onChange: (value: string) => void;
  choices?: { value: string; label: string }[];
  exclusiveOptions?: string[];
  error?: string | null;
  helpText?: string;
  disabled?: boolean;
  fieldId?: string;
  min?: number | null;
  max?: number | null;
  children?: ReactNode;
}

/** The existing product-intake controls, shared by both intake models. */
export function IntakeFormField(props: IntakeFormFieldProps) {
  const id = props.fieldId ?? `healthie-mod-${props.module.id}`;
  return (
    <fieldset
      aria-label={
        ["radio", "checkbox", "sex"].includes(
          archetypeForModule(props.module.mod_type),
        )
          ? props.module.label.replace(/<[^>]+>/g, "").trim()
          : undefined
      }
      id={id}
      disabled={props.disabled}
      aria-invalid={Boolean(props.error)}
      aria-describedby={
        [props.helpText && `${id}-help`, props.error && `${id}-error`]
          .filter(Boolean)
          .join(" ") || undefined
      }
      className={cn(
        "min-w-0 border-0 p-0 m-0",
        props.error &&
          "[&_label]:border-[#C81E1E] [&_input]:border-[#C81E1E] [&_textarea]:border-[#C81E1E] [&_button]:border-[#C81E1E] [&_select]:border-[#C81E1E]",
      )}
    >
      {props.children ? (
        <div className="flex flex-col gap-8">
          <FieldLabel
            label={props.module.label}
            required={props.module.required}
          />
          {props.children}
        </div>
      ) : (
        <FieldControl {...props} />
      )}
      {props.helpText && (
        <p id={`${id}-help`} className="type-body-sm text-ink-muted mt-8">
          {props.helpText}
        </p>
      )}
      {props.error && (
        <p
          id={`${id}-error`}
          role="alert"
          className="type-label-xs text-[#C81E1E] mt-8"
        >
          {props.error}
        </p>
      )}
    </fieldset>
  );
}

export function focusIntakeField(id: string) {
  const field = document.getElementById(id);
  const reduceMotion = window.matchMedia?.(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  field?.scrollIntoView?.({
    behavior: reduceMotion ? "auto" : "smooth",
    block: "center",
  });
  field
    ?.querySelector<HTMLElement>(
      "input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled])",
    )
    ?.focus({ preventScroll: true });
}

// Healthie's mod_type taxonomy is loose — we map known values into a handful
// of input archetypes. Anything unknown falls through to a text input so the
// form still works; clinicians review answers in Healthie regardless.
type Archetype =
  | "header"
  | "text"
  | "textarea"
  | "date"
  | "radio"
  | "checkbox"
  | "agree"
  | "address"
  | "sex"
  | "signature"
  | "height";

export function archetypeForModule(mod_type: string): Archetype {
  switch (mod_type) {
    case "read_only":
    case "label":
    case "body":
      return "header";
    case "textarea":
      return "textarea";
    case "date":
    case "dob":
      return "date";
    case "radio":
    case "horizontal_radio":
      return "radio";
    case "checkbox":
      return "checkbox";
    case "agree_to_above":
      return "agree";
    case "location":
      return "address";
    case "sex":
    case "gender":
    case "gender_identity":
      return "sex";
    case "signature":
      return "signature";
    case "Height (in.)":
      return "height";
    case "name":
    case "phone":
    case "text":
    case "client_source":
    case "Weight":
    default:
      return "text";
  }
}

export function parseOptions(raw: string): string[] {
  return (raw || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
}

function FieldControl({
  module: m,
  value,
  onChange,
  choices,
  exclusiveOptions = [],
  error,
  helpText,
  fieldId,
  min,
  max,
}: IntakeFormFieldProps) {
  const archetype = archetypeForModule(m.mod_type);
  const opts = useMemo(
    () => choices?.map((choice) => choice.value) ?? parseOptions(m.options),
    [choices, m.options],
  );
  const optionLabel = (value: string) =>
    choices?.find((choice) => choice.value === value)?.label ?? value;
  const stripHtml = (s: string) => s.replace(/<[^>]+>/g, "").trim();
  const label = stripHtml(m.label || "");
  const id = fieldId ?? `healthie-mod-${m.id}`;
  const common = {
    "aria-label": label,
    "aria-invalid": Boolean(error),
    "aria-required": m.required,
    "aria-describedby":
      [helpText && `${id}-help`, error && `${id}-error`]
        .filter(Boolean)
        .join(" ") || undefined,
  };

  if (archetype === "header") {
    if (!label && !m.options) return null;
    return (
      <div className="mt-5 first:mt-0">
        {label && (
          <div className="type-label-md text-ink-secondary">
            {label.toUpperCase()}
          </div>
        )}
        {m.options && (
          <p
            className="type-body-sm text-ink-secondary mt-5"
            dangerouslySetInnerHTML={{ __html: m.options }}
          />
        )}
      </div>
    );
  }

  if (archetype === "textarea") {
    return (
      <label className="flex flex-col gap-8">
        <FieldLabel label={label} required={m.required} />
        <textarea
          {...common}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          placeholder={hintForLabel(label)}
          className="px-20 py-15 bg-[#FAFAFA] border border-[#ECECEC] type-body-lg text-ink placeholder:text-[#4A4A4A] focus:outline-none"
        />
      </label>
    );
  }

  if (archetype === "date") {
    // NOTE: cannot use <label> here because it wraps THREE inputs (MM/DD/YYYY).
    // Per HTML spec a <label> with multiple form controls is associated with
    // the first one, so clicking DD/YYYY bubbles to the label and is redirected
    // to MM — DD/YYYY can never receive focus and the user can't type into them.
    // <fieldset> + a non-interactive label span is the correct grouping.
    return (
      <fieldset className="flex flex-col gap-8 border-0 p-0 m-0 min-w-0">
        <FieldLabel label={label} required={m.required} />
        <DateInput
          value={value}
          onChange={onChange}
          label={label}
          invalid={Boolean(error)}
          describedBy={common["aria-describedby"]}
          required={m.required}
        />
      </fieldset>
    );
  }

  if (archetype === "radio") {
    // Two flex-1 layouts split the row evenly with no wrapping:
    //   - Binary questions (Yes/No, etc.)
    //   - Numeric scales (1–5, 1–10, etc.) — every option parses as an int
    // Otherwise: content-sized chips that wrap when needed.
    const isBinary = opts.length === 2;
    const isNumericScale =
      opts.length > 0 && opts.every((o) => /^\d+$/.test(o));
    const fullWidth = isBinary || isNumericScale;
    return (
      <fieldset className="flex flex-col gap-10">
        <FieldLabel label={label} required={m.required} />
        <div className={cn("flex gap-8", fullWidth ? "" : "flex-wrap")}>
          {opts.map((opt) => {
            const active = value === opt;
            return (
              <button
                key={opt}
                type="button"
                aria-pressed={active}
                aria-describedby={common["aria-describedby"]}
                onClick={() => onChange(opt)}
                className={cn(
                  "min-h-44 py-10 px-10 text-[1.3rem] border",
                  fullWidth ? "flex-1 text-center" : "text-left",
                  active
                    ? "bg-[var(--surface-inverse)] text-[var(--ink-on-inverse)] border-[var(--surface-inverse)]"
                    : "bg-[#FAFAFA] text-[#4A4A4A] border-[#ECECEC] hover:border-ink",
                )}
              >
                {optionLabel(opt)}
              </button>
            );
          })}
        </div>
      </fieldset>
    );
  }

  if (archetype === "checkbox") {
    // Multi-select chip group. Healthie checkbox answers are the selected
    // option strings newline-joined — same encoding Healthie itself uses,
    // so clinician review renders them as a list. Binary options get the
    // radio's even two-across split.
    const selected = value ? value.split("\n").filter(Boolean) : [];
    const toggle = (opt: string) => {
      const next = selected.includes(opt)
        ? selected.filter((s) => s !== opt)
        : exclusiveOptions.includes(opt)
          ? [opt]
          : [
              ...selected.filter((item) => !exclusiveOptions.includes(item)),
              opt,
            ];
      onChange(next.join("\n"));
    };
    const fullWidth = opts.length === 2;
    return (
      <fieldset className="flex flex-col gap-10">
        <FieldLabel label={label} required={m.required} />
        <div className={cn("flex gap-8", fullWidth ? "" : "flex-wrap")}>
          {opts.map((opt) => {
            const active = selected.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                aria-pressed={active}
                aria-describedby={common["aria-describedby"]}
                onClick={() => toggle(opt)}
                className={cn(
                  "min-h-44 py-10 px-10 text-[1.3rem] border",
                  fullWidth ? "flex-1 text-center" : "text-left",
                  active
                    ? "bg-[var(--surface-inverse)] text-[var(--ink-on-inverse)] border-[var(--surface-inverse)]"
                    : "bg-[#FAFAFA] text-[#4A4A4A] border-[#ECECEC] hover:border-ink",
                )}
              >
                {optionLabel(opt)}
              </button>
            );
          })}
        </div>
      </fieldset>
    );
  }

  if (archetype === "agree") {
    // Healthie's form builder stamps agree_to_above modules with the
    // default label "Require client to agree" — that's builder-facing
    // copy (the statement itself is the preceding read-only module), so
    // patients should just see "I agree". And when the module's options
    // text merely repeats the label, drop the sub-line entirely instead
    // of printing the same phrase twice.
    const isDefaultLabel =
      label.trim().toLowerCase() === "require client to agree";
    const displayLabel = isDefaultLabel ? "I agree" : label;
    const rawAgreement = m.options ? stripHtml(m.options).trim() : "";
    const agreementText =
      rawAgreement &&
      rawAgreement.toLowerCase() !== label.trim().toLowerCase() &&
      rawAgreement.toLowerCase() !== displayLabel.trim().toLowerCase()
        ? rawAgreement
        : "";
    const checked = value === "Yes";
    return (
      <label
        className={cn(
          "flex items-start gap-15 cursor-pointer p-15 border",
          "bg-[var(--surface-page)] border-[#ECECEC] hover:border-ink focus-within:outline focus-within:outline-1 focus-within:outline-ink",
        )}
      >
        <input
          {...common}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked ? "Yes" : "")}
          className="sr-only peer"
        />
        <span
          aria-hidden
          className={cn(
            "w-32 h-32 shrink-0 flex items-center justify-center text-15",
            checked
              ? "bg-[var(--surface-inverse)] text-[var(--ink-on-inverse)]"
              : "bg-[#FAFAFA] border border-[#ECECEC]",
          )}
        >
          {checked && <span className="leading-none">✓</span>}
        </span>
        <div className="flex-1 min-w-0">
          {displayLabel && (
            <div className="type-label-md text-ink">
              {displayLabel.toUpperCase()}
            </div>
          )}
          {agreementText && (
            <p className="type-body-sm text-[#7C7C7C] mt-5">{agreementText}</p>
          )}
        </div>
      </label>
    );
  }

  if (archetype === "sex") {
    const effectiveOpts = opts.length
      ? opts
      : ["Male", "Female", "Other", "Prefer not to say"];
    const isBinary = effectiveOpts.length === 2;
    return (
      <fieldset className="flex flex-col gap-10">
        <FieldLabel label={label} required={m.required} />
        <div className={cn("flex gap-8", isBinary ? "" : "flex-wrap")}>
          {effectiveOpts.map((opt) => {
            const active = value === opt;
            return (
              <button
                key={opt}
                type="button"
                aria-pressed={active}
                aria-describedby={common["aria-describedby"]}
                onClick={() => onChange(opt)}
                className={cn(
                  "h-45 px-20 text-14 border text-center",
                  isBinary && "flex-1",
                  active
                    ? "bg-[var(--surface-inverse)] text-[var(--ink-on-inverse)] border-[var(--surface-inverse)]"
                    : "bg-[#FAFAFA] text-[#4A4A4A] border-[#ECECEC] hover:border-ink",
                )}
              >
                {optionLabel(opt)}
              </button>
            );
          })}
        </div>
      </fieldset>
    );
  }

  if (archetype === "address") {
    return (
      <label className="flex flex-col gap-8">
        <FieldLabel label={label || "Address"} required={m.required} />
        <textarea
          {...common}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          placeholder="Street, city, state, ZIP"
          className="px-20 py-15 bg-[#FAFAFA] border border-[#ECECEC] type-body-lg text-ink placeholder:text-[#4A4A4A] focus:outline-none"
        />
      </label>
    );
  }

  if (archetype === "signature") {
    return (
      <label className="flex flex-col gap-8">
        <FieldLabel
          label={label || "Type your full legal name to sign"}
          required={m.required}
        />
        <input
          {...common}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Full legal name"
          maxLength={200}
          className="h-50 px-20 bg-[#FAFAFA] border border-[#ECECEC] type-body-lg text-ink placeholder:text-[#4A4A4A] focus:outline-none"
        />
        <span className="type-label-xs text-ink-muted">
          By typing your name above, you electronically sign this form.
        </span>
      </label>
    );
  }

  if (archetype === "height") {
    return (
      <div className="flex flex-col gap-8">
        <FieldLabel label="Height" required={m.required} />
        <IntakeHeightInput
          value={value}
          onChange={onChange}
          label={label || "Height"}
          invalid={Boolean(error)}
          required={m.required}
          describedBy={common["aria-describedby"]}
        />
      </div>
    );
  }

  // Text fallback (covers `text`, `name`, `phone`, weight, client_source, unknowns)
  const inputType =
    m.mod_type === "phone"
      ? "tel"
      : m.mod_type === "number"
        ? "number"
        : "text";
  return (
    <label className="flex flex-col gap-8">
      <FieldLabel label={label} required={m.required} />
      <input
        {...common}
        type={inputType}
        min={min ?? undefined}
        max={max ?? undefined}
        step={inputType === "number" ? "any" : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={hintForLabel(label)}
        className="h-50 px-20 bg-[#FAFAFA] border border-[#ECECEC] type-body-lg text-ink placeholder:text-[#4A4A4A] focus:outline-none"
      />
    </label>
  );
}

/** Shared feet/inches controls; both forms store the total height in inches. */
export function IntakeHeightInput({
  value,
  onChange,
  label,
  invalid,
  required,
  describedBy,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
  invalid: boolean;
  required: boolean;
  describedBy?: string;
}) {
  const total = Number(value);
  const hasValue = Number.isFinite(total) && total > 0;
  const feet = hasValue ? Math.floor(total / 12) : NaN;
  const inches = hasValue ? total % 12 : NaN;
  const compose = (ftStr: string, inStr: string) => {
    const ft = parseFloat(ftStr);
    const inch = parseFloat(inStr);
    if (!Number.isFinite(ft) && !Number.isFinite(inch)) {
      onChange("");
      return;
    }
    onChange(
      String(
        (Number.isFinite(ft) ? ft : 0) * 12 +
          (Number.isFinite(inch) ? inch : 0),
      ),
    );
  };
  const numClasses =
    "h-50 px-20 w-full bg-[#FAFAFA] border border-[#ECECEC] type-body-lg text-ink placeholder:text-[#4A4A4A] focus:outline-none";
  return (
    <div className="flex gap-8 min-w-0">
      <label className="flex-1 min-w-0 flex flex-col gap-5">
        <input
          aria-label={`${label} feet`}
          aria-invalid={invalid}
          aria-required={required}
          aria-describedby={describedBy}
          type="number"
          inputMode="numeric"
          min={0}
          max={8}
          value={Number.isFinite(feet) ? String(feet) : ""}
          onChange={(e) =>
            compose(
              e.target.value,
              Number.isFinite(inches) ? String(inches) : "",
            )
          }
          placeholder="Feet"
          className={numClasses}
        />
        <span className="type-label-xs text-ink-muted">FEET</span>
      </label>
      <label className="flex-1 min-w-0 flex flex-col gap-5">
        <input
          aria-label={`${label} inches`}
          aria-invalid={invalid}
          aria-required={required}
          aria-describedby={describedBy}
          type="number"
          inputMode="numeric"
          min={0}
          max={11.9999}
          step="any"
          value={Number.isFinite(inches) ? String(inches) : ""}
          onChange={(e) =>
            compose(Number.isFinite(feet) ? String(feet) : "", e.target.value)
          }
          placeholder="Inches"
          className={numClasses}
        />
        <span className="type-label-xs text-ink-muted">INCHES</span>
      </label>
    </div>
  );
}

// Required questions carry the red asterisk; everything else says so, so a
// patient can tell what they may leave blank. Display-only and agree modules
// never render a FieldLabel.
function FieldLabel({ label, required }: { label: string; required: boolean }) {
  return (
    <span className="type-label-sm text-ink-secondary">
      {label.toUpperCase()}
      {required ? (
        <span className="text-[#C81E1E] ml-5">*</span>
      ) : (
        <span className="text-ink-muted ml-5">(optional)</span>
      )}
    </span>
  );
}

// Light contextual placeholder cues — better UX than a bare input box when
// the label alone is vague. Keep low-information; clinicians read the
// actual answer, not the placeholder.
function hintForLabel(label: string): string | undefined {
  const lower = label.toLowerCase();
  // Allergy first: "Any medication allergies?" asks for allergies, not a
  // medication list.
  if (lower.includes("allerg")) return "Medications, foods, environmental...";
  if (lower.includes("medication") || lower.includes("medicine"))
    return "Generic or brand name, doses, how often...";
  if (lower.includes("phone")) return "(555) 555-5555";
  if (lower.includes("email")) return "you@domain.com";
  if (
    lower.includes("goal") ||
    lower.includes("hoping") ||
    lower.includes("achieve")
  )
    return "Recovery, performance, longevity, sleep...";
  return undefined;
}

// MM / DD / YYYY split-field date input — used for any `date` / `dob`
// Healthie mod_type. Stores ISO YYYY-MM-DD as the value the backend
// expects. Local state holds the partial input so users can type "1"
// without the parent wiping the field (parent's `value` is '' until
// all 3 parts complete).
function DateInput({
  value,
  onChange,
  label,
  invalid,
  describedBy,
  required,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  invalid: boolean;
  describedBy?: string;
  required: boolean;
}) {
  const initial = useMemo(() => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
    return m
      ? { mm: m[2], dd: m[3], yyyy: m[1] }
      : { mm: "", dd: "", yyyy: "" };
  }, [value]);
  const [mm, setMm] = useState(initial.mm);
  const [dd, setDd] = useState(initial.dd);
  const [yyyy, setYyyy] = useState(initial.yyyy);

  const emittedValue = useRef<string | undefined>(undefined);
  // A partial local edit emits an empty canonical value. Keep the other date
  // parts for that echo, but hydrate all parts when an external draft changes.
  useEffect(() => {
    if (emittedValue.current === value) {
      emittedValue.current = undefined;
      return;
    }
    setMm(initial.mm);
    setDd(initial.dd);
    setYyyy(initial.yyyy);
  }, [value, initial]);

  const ddRef = useRef<HTMLInputElement | null>(null);
  const yyyyRef = useRef<HTMLInputElement | null>(null);

  const emit = (nextMm: string, nextDd: string, nextYyyy: string) => {
    if (nextMm.length === 2 && nextDd.length === 2 && nextYyyy.length === 4) {
      const next = `${nextYyyy}-${nextMm}-${nextDd}`;
      emittedValue.current = next;
      onChange(next);
    } else if (value) {
      emittedValue.current = "";
      onChange("");
    }
  };

  return (
    <div className="grid grid-cols-3 gap-8">
      <input
        type="text"
        inputMode="numeric"
        placeholder="MM"
        aria-label={`${label} month`}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        aria-required={required}
        value={mm}
        maxLength={2}
        onChange={(e) => {
          const v = e.target.value.replace(/\D/g, "").slice(0, 2);
          setMm(v);
          emit(v, dd, yyyy);
          if (v.length === 2) ddRef.current?.focus();
        }}
        className="h-50 px-15 bg-[#FAFAFA] border border-[#ECECEC] type-body-lg text-ink text-center placeholder:text-[#4A4A4A] focus:outline-none"
      />
      <input
        ref={ddRef}
        type="text"
        inputMode="numeric"
        placeholder="DD"
        aria-label={`${label} day`}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        aria-required={required}
        value={dd}
        maxLength={2}
        onChange={(e) => {
          const v = e.target.value.replace(/\D/g, "").slice(0, 2);
          setDd(v);
          emit(mm, v, yyyy);
          if (v.length === 2) yyyyRef.current?.focus();
        }}
        className="h-50 px-15 bg-[#FAFAFA] border border-[#ECECEC] type-body-lg text-ink text-center placeholder:text-[#4A4A4A] focus:outline-none"
      />
      <input
        ref={yyyyRef}
        type="text"
        inputMode="numeric"
        placeholder="YYYY"
        aria-label={`${label} year`}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        aria-required={required}
        value={yyyy}
        maxLength={4}
        onChange={(e) => {
          const v = e.target.value.replace(/\D/g, "").slice(0, 4);
          setYyyy(v);
          emit(mm, dd, v);
        }}
        className="h-50 px-15 bg-[#FAFAFA] border border-[#ECECEC] type-body-lg text-ink text-center placeholder:text-[#4A4A4A] focus:outline-none"
      />
    </div>
  );
}
